/**
 * React Query hook for the platform/placement spec registry.
 *
 * The registry rarely changes — fetched once per (platform, ad_type) and cached
 * for the session. Use this in any UI that needs to know "what placements are
 * valid for this ad" or "what aspect ratio + dims does this placement want".
 */
import { useQuery } from '@tanstack/react-query';

import {
  listPlatformSpecs,
  type ListPlatformSpecsParams,
} from '@/services/api/platformSpecs';
import type { PlatformPlacementSpec } from '@/types/platformSpecs';

export const platformSpecsQueryKeys = {
  all: ['platform-specs'] as const,
  list: (params: ListPlatformSpecsParams) =>
    [...platformSpecsQueryKeys.all, params] as const,
} as const;

export function usePlatformSpecs(params: ListPlatformSpecsParams = {}) {
  return useQuery<PlatformPlacementSpec[]>({
    queryKey: platformSpecsQueryKeys.list(params),
    queryFn: () => listPlatformSpecs(params),
    // Registry is code-defined backend-side. Treat it as effectively static
    // within a session — refetch only on remount or window focus after a long idle.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

/** Look up a spec by key from a loaded list. Returns undefined if not found. */
export function findSpecByKey(
  specs: PlatformPlacementSpec[] | undefined,
  key: string
): PlatformPlacementSpec | undefined {
  return specs?.find((s) => s.key === key);
}
